import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft, Building2, Mail, Lock, User, Phone, MapPin, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const logoUrl = "https://customer-assets.emergentagent.com/job_e65197a9-8183-40fd-a17c-c3a01c1869d3/artifacts/0f0od067_Memorial%20Desk.webp";

function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, API } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  
  // Check URL params for tab selection and industry type
  const [industryType, setIndustryType] = useState('memorials');
  
  useEffect(() => {
    const tab = searchParams.get('tab');
    const industry = searchParams.get('industry');
    if (tab === 'register') {
      setActiveTab('register');
    }
    if (industry === 'worktops' || industry === 'memorials') {
      setIndustryType(industry);
    }
  }, [searchParams]);
  
  // Login form
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  
  // Business registration form
  const [registerData, setRegisterData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    address: '',
    password: '',
    confirmPassword: ''
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: loginData.email,
        password: loginData.password
      });
      login(response.data.user, response.data.token);
      toast.success('Welcome back!');
      
      if (response.data.user.role === 'trade') {
        navigate('/trade-dashboard');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Invalid email or password');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerData.password !== registerData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (registerData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);

    try {
      // Register as a new company with trial - include industry type
      const response = await axios.post(`${BACKEND_URL}/api/companies/register`, {
        company_name: registerData.companyName,
        contact_name: registerData.contactName,
        email: registerData.email,
        phone: registerData.phone,
        address: registerData.address,
        password: registerData.password,
        industry_type: industryType  // 'memorials' or 'worktops'
      });
      
      setRegistrationSuccess(true);
      toast.success('Account created! Check your email for confirmation.');
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Registration failed. Please try again.');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (registrationSuccess) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4"
           style={{backgroundImage: "radial-gradient(circle at 20% 30%, rgba(201,162,74,0.08) 0%, transparent 55%), radial-gradient(circle at 80% 70%, rgba(201,162,74,0.06) 0%, transparent 55%)"}}>
        <Card className="w-full max-w-md bg-[#111111] border-[#c9a24a]/30">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-[#c9a24a]/20 rounded-full flex items-center justify-center mx-auto mb-6 ring-1 ring-[#c9a24a]/40">
              <CheckCircle className="w-8 h-8 text-[#c9a24a]" />
            </div>
            <img src={logoUrl} alt="Memorial Desk" className="h-20 w-auto mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-white mb-2">Welcome to Memorial Desk!</h2>
            <p className="text-slate-400 mb-6">
              Your account has been created with a 30-day free trial. 
              You can now sign in and start managing your memorial business.
            </p>
            <Button 
              onClick={() => {
                setRegistrationSuccess(false);
                setActiveTab('login');
                setLoginData({ email: registerData.email, password: '' });
              }}
              className="w-full bg-[#c9a24a] hover:bg-[#d9b25a] text-black font-semibold tracking-wide"
            >
              Sign In Now
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex"
         style={{backgroundImage: "radial-gradient(circle at 15% 25%, rgba(201,162,74,0.10) 0%, transparent 45%), radial-gradient(circle at 85% 80%, rgba(201,162,74,0.06) 0%, transparent 50%)"}}>
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden border-r border-[#c9a24a]/15">
        {/* Marble-vein texture overlay */}
        <div className="absolute inset-0 opacity-[0.05]"
             style={{backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='400' height='400'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.79 0 0 0 0 0.63 0 0 0 0 0.29 0 0 0 0.4 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"}} />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-[#c9a24a]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-[#c9a24a]/8 rounded-full blur-3xl" />
        
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-20">
          <Link to="/" className="mb-10 block">
            <img src={logoUrl} alt="Memorial Desk" className="h-24 xl:h-32 w-auto" />
          </Link>
          
          <div className="text-xs tracking-[0.35em] text-[#c9a24a] font-semibold mb-4">
            MANAGE. DESIGN. GROW.
          </div>
          <h1 className="text-4xl xl:text-5xl font-bold text-white mb-4 leading-tight">
            Software built for
            <br />
            <span className="text-[#c9a24a]">
              memorial masons
            </span>
          </h1>
          <div className="w-24 h-[2px] bg-gradient-to-r from-[#c9a24a] to-transparent mb-6" />
          
          <p className="text-lg text-slate-300 mb-8 max-w-md">
            Join memorial masons across the UK who've streamlined their operations with Memorial Desk.
          </p>
          
          <div className="space-y-4">
            {[
              'Unlimited orders and quotes',
              'Artwork approval workflow',
              'Professional invoicing',
              '30-day free trial'
            ].map((feature, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-[#c9a24a]/15 flex items-center justify-center ring-1 ring-[#c9a24a]/40">
                  <CheckCircle className="w-3.5 h-3.5 text-[#c9a24a]" />
                </div>
                <span className="text-slate-200">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Right Panel - Forms */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <Link to="/">
              <img src={logoUrl} alt="Memorial Desk" className="h-20 w-auto mx-auto mb-4" />
            </Link>
          </div>
          
          {/* Back Link */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-400 hover:text-[#c9a24a] mb-6 transition-colors"
            data-testid="back-to-home-link"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>
          
          <Card className="bg-[#111111] border-[#c9a24a]/25 shadow-2xl shadow-[#c9a24a]/5">
            <CardHeader className="pb-4">
              <CardTitle className="text-2xl text-white">
                {activeTab === 'login' ? 'Welcome back' : 'Start your free trial'}
              </CardTitle>
              <CardDescription className="text-slate-400">
                {activeTab === 'login' 
                  ? 'Sign in to your Memorial Desk account' 
                  : 'Create your business account - 30 days free'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6 bg-[#0a0a0a] border border-[#c9a24a]/20">
                  <TabsTrigger 
                    value="login" 
                    data-testid="login-tab"
                    className="data-[state=active]:bg-[#c9a24a] data-[state=active]:text-black text-slate-300"
                  >
                    Sign In
                  </TabsTrigger>
                  <TabsTrigger 
                    value="register" 
                    data-testid="register-tab"
                    className="data-[state=active]:bg-[#c9a24a] data-[state=active]:text-black text-slate-300"
                  >
                    Register
                  </TabsTrigger>
                </TabsList>

                {/* Login Form */}
                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                    <div className="space-y-2">
                      <Label htmlFor="login-email" className="text-slate-300">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <Input
                          id="login-email"
                          type="email"
                          placeholder="you@company.com"
                          value={loginData.email}
                          onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                          required
                          data-testid="login-email-input"
                          className="pl-10 bg-[#1a1a1a] border-[#c9a24a]/25 text-white placeholder:text-slate-500 focus-visible:ring-[#c9a24a]/50"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="login-password" className="text-slate-300">Password</Label>
                        <Link 
                          to="/forgot-password" 
                          className="text-sm text-[#c9a24a] hover:text-[#e0b95a]"
                        >
                          Forgot password?
                        </Link>
                      </div>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <Input
                          id="login-password"
                          type={showPassword ? "text" : "password"}
                          placeholder="••••••••"
                          value={loginData.password}
                          onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                          required
                          data-testid="login-password-input"
                          className="pl-10 pr-10 bg-[#1a1a1a] border-[#c9a24a]/25 text-white placeholder:text-slate-500 focus-visible:ring-[#c9a24a]/50"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-[#c9a24a] hover:bg-[#d9b25a] text-black font-semibold tracking-wide" 
                      disabled={loading} 
                      data-testid="login-submit-btn"
                    >
                      {loading ? 'Signing in...' : 'Sign In'}
                    </Button>
                  </form>
                  
                  <div className="mt-6 text-center">
                    <p className="text-sm text-slate-500">
                      Don't have an account?{' '}
                      <button 
                        onClick={() => setActiveTab('register')}
                        className="text-[#c9a24a] hover:text-[#e0b95a]"
                      >
                        Start free trial
                      </button>
                    </p>
                  </div>
                </TabsContent>

                {/* Register Form */}
                <TabsContent value="register">
                  <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
                    {/* Industry Type Selector */}
                    <div className="mb-4">
                      <Label className="text-slate-300 mb-2 block">I'm signing up for:</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => setIndustryType('memorials')}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            industryType === 'memorials'
                              ? 'border-[#c9a24a] bg-[#c9a24a]/10 text-white'
                              : 'border-[#c9a24a]/20 bg-[#1a1a1a] text-slate-400 hover:border-[#c9a24a]/50'
                          }`}
                        >
                          <div className="font-semibold">Memorial Masons</div>
                          <div className="text-xs opacity-70">Memorials & headstones</div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setIndustryType('worktops')}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            industryType === 'worktops'
                              ? 'border-orange-500 bg-orange-500/10 text-white'
                              : 'border-[#c9a24a]/20 bg-[#1a1a1a] text-slate-400 hover:border-[#c9a24a]/50'
                          }`}
                        >
                          <div className="font-semibold">Stone Fabricators</div>
                          <div className="text-xs opacity-70">Granite, marble & quartz</div>
                        </button>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="register-company" className="text-slate-300">Business Name</Label>
                      <div className="relative">
                        <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <Input
                          id="register-company"
                          type="text"
                          placeholder={industryType === 'memorials' ? "Your Memorial Company Ltd" : "Your Stone Company Ltd"}
                          value={registerData.companyName}
                          onChange={(e) => setRegisterData({ ...registerData, companyName: e.target.value })}
                          required
                          data-testid="register-company-input"
                          className="pl-10 bg-[#1a1a1a] border-[#c9a24a]/25 text-white placeholder:text-slate-500 focus-visible:ring-[#c9a24a]/50"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="register-name" className="text-slate-300">Your Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                        <Input
                          id="register-name"
                          type="text"
                          placeholder="John Smith"
                          value={registerData.contactName}
                          onChange={(e) => setRegisterData({ ...registerData, contactName: e.target.value })}
                          required
                          data-testid="register-name-input"
                          className="pl-10 bg-[#1a1a1a] border-[#c9a24a]/25 text-white placeholder:text-slate-500 focus-visible:ring-[#c9a24a]/50"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="register-email" className="text-slate-300">Email</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                          <Input
                            id="register-email"
                            type="email"
                            placeholder="you@company.com"
                            value={registerData.email}
                            onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                            required
                            data-testid="register-email-input"
                            className="pl-10 bg-[#1a1a1a] border-[#c9a24a]/25 text-white placeholder:text-slate-500 focus-visible:ring-[#c9a24a]/50"
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="register-phone" className="text-slate-300">Phone</Label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                          <Input
                            id="register-phone"
                            type="tel"
                            placeholder="07123 456789"
                            value={registerData.phone}
                            onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                            data-testid="register-phone-input"
                            className="pl-10 bg-[#1a1a1a] border-[#c9a24a]/25 text-white placeholder:text-slate-500 focus-visible:ring-[#c9a24a]/50"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="register-address" className="text-slate-300">Business Address</Label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-3 w-4 h-4 text-slate-500" />
                        <Input
                          id="register-address"
                          type="text"
                          placeholder="123 High Street, Town, AB1 2CD"
                          value={registerData.address}
                          onChange={(e) => setRegisterData({ ...registerData, address: e.target.value })}
                          data-testid="register-address-input"
                          className="pl-10 bg-[#1a1a1a] border-[#c9a24a]/25 text-white placeholder:text-slate-500 focus-visible:ring-[#c9a24a]/50"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="register-password" className="text-slate-300">Password</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                          <Input
                            id="register-password"
                            type="password"
                            placeholder="Min 8 characters"
                            value={registerData.password}
                            onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                            required
                            data-testid="register-password-input"
                            className="pl-10 bg-[#1a1a1a] border-[#c9a24a]/25 text-white placeholder:text-slate-500 focus-visible:ring-[#c9a24a]/50"
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="register-confirm" className="text-slate-300">Confirm Password</Label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                          <Input
                            id="register-confirm"
                            type="password"
                            placeholder="Confirm password"
                            value={registerData.confirmPassword}
                            onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                            required
                            data-testid="register-confirm-input"
                            className="pl-10 bg-[#1a1a1a] border-[#c9a24a]/25 text-white placeholder:text-slate-500 focus-visible:ring-[#c9a24a]/50"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-[#c9a24a]/10 border border-[#c9a24a]/25 rounded-lg p-3">
                      <p className="text-sm text-[#e0b95a]">
                        <strong className="text-[#c9a24a]">30-day free trial</strong> — No credit card required. 
                        Full access to all features.
                      </p>
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-[#c9a24a] hover:bg-[#d9b25a] text-black font-semibold tracking-wide" 
                      disabled={loading} 
                      data-testid="register-submit-btn"
                    >
                      {loading ? 'Creating account...' : 'Start Free Trial'}
                    </Button>
                    
                    <p className="text-xs text-slate-500 text-center">
                      By registering, you agree to our Terms of Service and Privacy Policy.
                    </p>
                  </form>
                  
                  <div className="mt-6 text-center">
                    <p className="text-sm text-slate-500">
                      Already have an account?{' '}
                      <button 
                        onClick={() => setActiveTab('login')}
                        className="text-[#c9a24a] hover:text-[#e0b95a]"
                      >
                        Sign in
                      </button>
                    </p>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
          
          {/* Footer */}
          <p className="text-center text-slate-600 text-sm mt-6">
            © {new Date().getFullYear()} memorialdesk.co.uk • A product by Set in Stone Didcot
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
